import React, { ReactNode, useState, useCallback, cloneElement, isValidElement } from 'react';
import styled from 'styled-components';
import { CollapseItemProps } from './collapseItem';

const CollapseContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: transparent;
`;

export interface CollapseProps {
  children: ReactNode;
  defaultExpandedNames?: (string | number)[];
  expandedNames?: (string | number)[];
  accordion?: boolean;
  onChange?: (expandedNames: (string | number)[]) => void;
}

export const UiCollapse: React.FC<CollapseProps> = ({
  children,
  defaultExpandedNames = [],
  expandedNames,
  accordion = false,
  onChange,
}) => {
  const [internalExpandedNames, setInternalExpandedNames] = useState<(string | number)[]>(defaultExpandedNames);

  const isControlled = expandedNames !== undefined;
  const currentExpandedNames = isControlled ? expandedNames : internalExpandedNames;

  const handleToggle = useCallback(
    (name: string | number, expanded: boolean) => {
      let newExpandedNames: (string | number)[];

      if (accordion) {
        newExpandedNames = expanded ? [name] : [];
      } else if (expanded) {
        newExpandedNames = [...currentExpandedNames, name];
      } else {
        newExpandedNames = currentExpandedNames.filter((n) => n !== name);
      }

      if (!isControlled) {
        setInternalExpandedNames(newExpandedNames);
      }
      onChange?.(newExpandedNames);
    },
    [accordion, currentExpandedNames, isControlled, onChange],
  );

  return (
    <CollapseContainer>
      {React.Children.map(children, (child) => {
        if (!isValidElement<CollapseItemProps>(child)) {
          return null;
        }

        const name = child.props.name ?? Math.random().toString();
        const expanded = currentExpandedNames.includes(name);

        return cloneElement(child, {
          name,
          expanded,
          onToggle: (expanded: boolean) => handleToggle(name, expanded),
        });
      })}
    </CollapseContainer>
  );
};
